import './App.css';
import NavigationBar from './navbar/NavigationBar';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import { createContext, useContext, useMemo, useCallback, useState } from 'react';
import { Outlet } from 'react-router-dom'; // useNavigate
import BackgroundImage from './shared/BackgroundImage';

const NUM_IMAGES = 8;
const NUM_QUOTES = 8;

const bgURLs = [
  { idx: 0, url: './assets/images/bg/hawaii.jpg', lqip: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="12"><rect width="100%" height="100%" fill="%23a7f3d0"/></svg>' },
  { idx: 1, url: './assets/images/bg/dolphins.jpg', lqip: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="12"><rect width="100%" height="100%" fill="%2300b4d8"/></svg>' },
  { idx: 2, url: './assets/images/bg/clouds.jpg', lqip: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="12"><rect width="100%" height="100%" fill="%23c7d2fe"/></svg>' },
  { idx: 3, url: './assets/images/bg/wheat.jpg', lqip: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="12"><rect width="100%" height="100%" fill="%23fef3c7"/></svg>' },
  { idx: 4, url: './assets/images/bg/cloud-sea.jpg', lqip: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="12"><rect width="100%" height="100%" fill="%239be7ff"/></svg>' },
  { idx: 5, url: './assets/images/bg/mountain-side.jpg', lqip: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="12"><rect width="100%" height="100%" fill="%2398f5e1"/></svg>' },
  { idx: 6, url: './assets/images/bg/sea-waves.jpg', lqip: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="12"><rect width="100%" height="100%" fill="%2300aaff"/></svg>' },
  { idx: 7, url: './assets/images/bg/rock-flowers.jpg', lqip: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="12"><rect width="100%" height="100%" fill="%23fbcfe8"/></svg>' },
];

const quotes = [
  { idx: 0, author: 'Og Mandino', text: 'Always do your best. What you plant now, you will harvest later.', explanation: 'This quote reminds us that consistent effort matters. The work you do today shapes the opportunities of tomorrow.' },
  { idx: 1, author: 'Marcus Tullius Cicero', text: 'More is lost by indecision than wrong decision.', explanation: 'Choosing nothing is often worse than making a mistake. Progress comes from action, even if the path is imperfect.' },
  { idx: 2, author: 'Vince Lombardi', text: 'Perfection is not attainable. But if we chase perfection we can catch excellence.', explanation: 'This encourages aiming high without being paralyzed by perfection. Great results come from striving for the best, not waiting for perfect.' },
  { idx: 3, author: 'Warren Buffet', text: `Someone's sitting in the shade today because someone planted a tree a long time ago.`, explanation: 'Long-term effort pays off. The benefits we enjoy now are often the result of investments and care taken long before.' },
  { idx: 4, author: 'Shakespeare', text: 'When the sea was calm, all ships alike showed mastership in floating.', explanation: 'True skill is seen in difficult conditions. Calm times hide differences, but challenges reveal who can handle them.' },
  { idx: 5, author: 'Neale Donald Walsch', text: 'Life begins at the end of your comfort zone.', explanation: 'Growth happens when you push past what feels safe. New experiences and learning start once you take a risk.' },
  { idx: 6, author: 'H. Jackson Brown, Jr.', text: 'The best preparation for tomorrow is doing your best today.', explanation: 'The actions you take now build tomorrow’s foundation. Preparing well in the present creates future readiness.' },
  { idx: 7, author: 'Francis of Assisi', text: `Start by doing what's necessary; then do what's possible; and suddenly you are doing the impossible.`, explanation: 'Big achievements begin with small practical steps. By handling what you can now, you eventually open the door to much more.' },
];

export const BackgroundContext = createContext(null);

export function AppLayout() {
  const startingIndex = useMemo(() => {
    const savedIndex = Number(sessionStorage.getItem('home-bg-index'));
    return Number.isFinite(savedIndex) ? savedIndex : Math.floor(Math.random() * NUM_IMAGES);
  }, []);

  const [bgIndex, setBgIndex] = useState(startingIndex);
  const [backgroundUrl, setBackgroundUrl] = useState(() => {
    const savedUrl = sessionStorage.getItem('home-bg-url');
    return savedUrl || bgURLs[startingIndex].url;
  });
  const [backgroundLqip, setBackgroundLqip] = useState(() => {
    const savedLqip = sessionStorage.getItem('home-bg-lqip');
    return savedLqip || bgURLs[startingIndex].lqip;
  });

  const shuffleBackground = useCallback((nextIndexOverride) => {
    const nextIndex = typeof nextIndexOverride === 'number' ? nextIndexOverride : (bgIndex + 1) % NUM_IMAGES;
    const nextImage = bgURLs[nextIndex];

    setBgIndex(nextIndex);
    setBackgroundUrl(nextImage.url);
    setBackgroundLqip(nextImage.lqip);
    sessionStorage.setItem('home-bg-index', String(nextIndex));
    sessionStorage.setItem('home-bg-url', nextImage.url);
    sessionStorage.setItem('home-bg-lqip', nextImage.lqip);
  }, [bgIndex]);

  const contextValue = useMemo(
    () => ({ bgIndex, backgroundUrl, backgroundLqip, shuffleBackground }),
    [bgIndex, backgroundUrl, backgroundLqip, shuffleBackground]
  );

  return (
    <BackgroundContext.Provider value={contextValue}>
      <BackgroundImage src={backgroundUrl} lqip={backgroundLqip} placeholderColor="#0f172a" data-home-background="true">
        <Outlet />
      </BackgroundImage>
    </BackgroundContext.Provider>
  );
}

function App() {
  const { bgIndex, shuffleBackground } = useContext(BackgroundContext);
  // const navigate = useNavigate();
  const quote = quotes[bgIndex % NUM_QUOTES];
  const [showQuoteExplanation, setShowQuoteExplanation] = useState(false);

  const handleShuffle = event => {
    event?.preventDefault();
    shuffleBackground();
    setShowQuoteExplanation(false);
  };

  const handleToggleQuoteExplanation = event => {
    event?.stopPropagation();
    setShowQuoteExplanation(prev => !prev);
  };

  // const navigateTo = path => event => {
  //   event?.preventDefault();
  //   navigate(path);
  // };

  return (
    <>
      <NavigationBar bgFn={handleShuffle} />
      <div className="hero-overlay" />
      <Container className="hero-container">
        <section className="hero-panel">
          <div className="hero-copy">
            <span className="hero-eyebrow">Interactive playground</span>
            <h1>A homepage built to explore, experience, and enjoy.</h1>
            <p className="hero-text">
              This is not only a showcase of skills. It is a living landing page with dynamic moods,
              playful interactions, and content designed to invite visitors to stay a little longer.
            </p>

            <div className="hero-actions">
              <Button variant="light" size="lg" onClick={handleShuffle}>Shuffle scenery</Button>
              {/* <Button variant="outline-light" size="lg" onClick={navigateTo('/blogs')}>Visit the blog</Button> */}
            </div>

            <div className="hero-badges">
              <Badge bg="light" text="dark">React</Badge>
              <Badge bg="light" text="dark">Interactive UI</Badge>
              <Badge bg="light" text="dark">Creative design</Badge>
              <Badge bg="light" text="dark">Story-driven</Badge>
            </div>
          </div>

          <Card className="hero-card">
            <Card.Body>
              <Card.Title>Playful engine</Card.Title>
              <Card.Text>
                Enjoy random scenery swaps, quick navigation, and an immersive welcome experience that feels more like a mini app than a static homepage.
              </Card.Text>
              <div className="hero-card-list">
                <span>Responsive layout</span>
                <span>Dynamic visuals</span>
                <span>Instant visitor delight</span>
              </div>
              <Button variant="outline-light" onClick={handleShuffle} className="hero-card-button">
                Refresh mood
              </Button>
            </Card.Body>
          </Card>
        </section>

        <section className="feature-grid">
          <Card className="feature-card">
            <Card.Body>
              <Card.Title>Scene switcher</Card.Title>
              <Card.Text>Move through vibrant background scenes and keep the homepage fresh every time.</Card.Text>
            </Card.Body>
          </Card>
          <Card className="feature-card">
            <Card.Body>
              <Card.Title>Quote discovery</Card.Title>
              <Card.Text>Each refresh reveals a new thoughtful quote, giving visitors a feeling of discovery.</Card.Text>
            </Card.Body>
          </Card>
          <Card className="feature-card">
            <Card.Body>
              <Card.Title>Jump into content</Card.Title>
              <Card.Text>Use the navigation to explore blogs, projects and demos powered by the same codebase.</Card.Text>
            </Card.Body>
          </Card>
        </section>

        <section className="quote-row">
          <Card className="quote-card" onClick={handleToggleQuoteExplanation} style={{ cursor: 'pointer' }}>
            <Card.Header>
              Current mood
              <span className="quote-hint">click to explain</span>
            </Card.Header>
            <Card.Body>
              <blockquote className="blockquote mb-0">
                <p>{quote.text}</p>
                <footer className="blockquote-footer">{quote.author}</footer>
              </blockquote>
              <div className="quote-actions">
                <Button variant="outline-light" size="sm" onClick={handleToggleQuoteExplanation} aria-expanded={showQuoteExplanation}>
                  {showQuoteExplanation ? 'Hide explanation' : 'Explain this mood'}
                </Button>
              </div>
              {showQuoteExplanation && (
                <div className="quote-explanation">
                  {quote.explanation}
                </div>
              )}
            </Card.Body>
          </Card>
        </section>
      </Container>
    </>
  );
}

export default App;
